import { useState, useRef, useEffect } from 'react';

interface UseScrollableLoadingResult<T> {
    visibleItems: number;
    containerRef: React.RefObject<T>;
    loadingRef: React.RefObject<HTMLDivElement>;
}

export const useScrollableLoading = <T extends HTMLElement = HTMLDivElement>(
    initialCount: number = 8,
    increment: number = 8,
    dependencies: any[] = [],
): UseScrollableLoadingResult<T> => {
    const [visibleItems, setVisibleItems] = useState<number>(initialCount);
    const containerRef = useRef<T>(null);
    const loadingRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const containerElement = containerRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    setVisibleItems((prev) => prev + increment);
                }
            },
            {
                threshold: 0.1,
                root: containerElement,
            },
        );

        if (loadingRef.current) {
            observer.observe(loadingRef.current);
        }

        return () => {
            if (loadingRef.current) {
                observer.unobserve(loadingRef.current);
            }
        };
    }, [...dependencies]);

    return {
        visibleItems,
        containerRef,
        loadingRef,
    };
};
